import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom'
import { useAppSelector } from 'app/hooks'
import { Box, Flex, Spinner } from '@chakra-ui/react'
import { BbButton, TextInput } from 'Core'
import { routeNames } from 'Constants/routeNames'
import LogoLoader from 'Components/LogoLoader/LogoLoader'

const AboutUs = lazy(() => import('./Pages').then((module) => ({ default: module.AboutUs })))
const Home = lazy(() => import('./Pages').then((module) => ({ default: module.Home })))

const BankLogin = lazy(() => import('Pages/Life').then((module) => ({ default: module.BankLogin })))

const SuitabilityMatrixSteps = lazy(() => import('Pages/Life/SuitabilityMatrixSteps'))
const RecommendedPlans = lazy(() => import('Pages/Life/RecommendedPlans'))
const BobLifeStepper = lazy(() => import('Pages/Life/BobLifeSteppers'))
const LifeQuotation = lazy(() => import('Pages/Life/Quotation'))
const RoleSelector = lazy(() => import('Pages/Common/RoleSelector'))
const SalesReport = lazy(() => import('Pages/Common/Home/SalesReport'))

const SpLicensing = lazy(() => import('Pages/Common/SpLicensing'))
const SpLicensingMcq = lazy(() => import('Pages/Common/SpLicensing/Mcq'))
const SpLicensingTakeExam = lazy(() => import('Pages/Common/SpLicensing/TakeExam'))
const SpLicensingExamResult = lazy(() => import('Pages/Common/SpLicensing/ExamResult'))
const SpLicensingManageQuestions = lazy(() => import('Pages/Common/SpLicensing/ManageQuestions'))
const SpLicensingAdminQuestions = lazy(
  () => import('Pages/Common/SpLicensing/AdminUploadQuestions'),
)

const LeadListing = lazy(() => import('Pages/Common/Home/LeadListing/LeadListing'))
const SellJourneyLayout = lazy(
  () => import('Pages/Common/Home/Sell_insuranceJourney/sellJourneyLayout'),
)
const QuotationPage = lazy(() =>
  import('Pages/Health').then((module) => ({ default: module.QuotationPage })),
)
const ProposalPage = lazy(() => import('Pages/Health/ProposalPage'))
const ProposerDetails = lazy(
  () => import('Pages/Health/ProposalPage/ProposerAndInsurerDetails/ProposerDetails'),
)
const MedicalHealthQuestions = lazy(
  () => import('Pages/Health/ProposalPage/HealthQuestions/MedicalHealthQuestions'),
)
const Addons = lazy(() => import('Pages/Health/ProposalPage/AddOns'))
const HealthCKYC = lazy(() =>
  import('Pages/Health/CKYC').then((module) => ({ default: module.HealthCKYC })),
)
const MemberDetails = lazy(() => import('Pages/Health/MemberDetails'))
const VehicleInsuranceDetails = lazy(() =>
  import('Pages/Motor/VehicleInsuranceDetails').then((module) => ({
    default: module.VehicleInsuranceDetails,
  })),
)
const Quotation = lazy(() =>
  import('Pages/Motor/Quotation').then((module) => ({ default: module.Quotation })),
)
const MotorProposal = lazy(() =>
  import('Pages/Motor').then((module) => ({ default: module.MotorProposal })),
)
const MotorSalesReport = lazy(() =>
  import('Pages/Motor').then((module) => ({ default: module.MotorSalesReport })),
)
const CKYC = lazy(() => import('Pages/Motor').then((module) => ({ default: module.CKYC })))
const Payment = lazy(() =>
  import('Pages/Health/ProposalPage/Payment').then((module) => ({ default: module.Payment })),
)
const CustomerConsentForm = lazy(() =>
  import('Pages/Motor/ConsentForm/CustomerConsentForm').then((module) => ({
    default: module.CustomerConsentForm,
  })),
)
const SpConsentForm = lazy(() =>
  import('Pages/Motor/ConsentForm/SpConsentForm').then((module) => ({
    default: module.SpConsentForm,
  })),
)
const Test = lazy(() => import('Pages/Motor/test'))
const ConsentForm = lazy(() =>
  import('Pages/Health/Consent').then((module) => ({
    default: module.ConsentForm,
  })),
)
const CustomerConsent = lazy(
  () => import('Pages/Common/Home/Sell_insuranceJourney/CustomerConsent'),
)
const PropertyDetails = lazy(() => import('Pages/Sachet/BGR/PropertyDetails'))
const QuotationBgr = lazy(() => import('Pages/Sachet/BGR/QuotationBgr'))
const ProposalDetails = lazy(() => import('Pages/Sachet/BGR/ProposalDetails'))
const BgrProposalCustomerConsent = lazy(
  () => import('Pages/Sachet/BGR/CustomerConsent/proposalCustomerConsent'),
)
const BgrPayment = lazy(() =>
  import('Pages/Sachet/BGR/Payment/index').then((module) => ({ default: module.BgrPayment })),
)
const BgrInitialCustomerConsent = lazy(
  () => import('Pages/Sachet/BGR/CustomerConsent/initialCustomerConsent'),
)
const BgrSpTagging = lazy(() => import('Pages/Sachet/BGR/SpTagging'))
const Customer360 = lazy(() => import('Pages/Customer360'))
const TrackRequest = lazy(() => import('Pages/Customer360/TrackRequest'))
const PaymentSelection = lazy(() => import('Pages/Customer360/PaymentSelection'))
const ClaimForm = lazy(() => import('Pages/Customer360/ClaimForm'))
const PaymentMethod = lazy(() =>
  import('Pages/Health/ProposalPage/PaymentMethod').then((module) => ({
    default: module.PaymentMethod,
  })),
)
const LifeStyleHealthQuestions = lazy(() =>
  import('Pages/Health/ProposalPage/HealthQuestions/LifeStyleHealthQuestions').then((module) => ({
    default: module.LifeStyleHealthQuestions,
  })),
)
const InsuredDetails = lazy(() =>
  import('Pages/Health/ProposalPage/ProposerAndInsurerDetails/InsuredDetails').then((module) => ({
    default: module.InsuredDetails,
  })),
)
const HealthSalesReport = lazy(() => import('Pages/Common/Home/SalesReport/HealthSales'))
const SalesReportBgr = lazy(() => import('Pages/Sachet/BGR/SalesReports'))
const FirePropertyQuestions = lazy(() =>
  import('Pages/Fire').then((module) => ({
    default: module.FirePropertyQuestions,
  })),
)
const CkycPage = lazy(() =>
  import('Pages/Fire/CKYC').then((module) => ({ default: module.CkycPage })),
)
const QuotePage = lazy(() => import('Pages/Fire/QuotePage/QuotePage'))
const FireJourneyGuard = lazy(() => import('Pages/Fire/FireJourneyGuard'))
const FireProposalCustomerConsent = lazy(() => import('Pages/Fire/Consent'))
const FireSpTagging = lazy(() => import('Pages/Fire/SpTagging'))
const ProposalPageFire = lazy(() =>
  import('Pages/Fire/Proposal').then((module) => ({ default: module.ProposalPageFire })),
)
const DetailsPage = lazy(() => import('Pages/Group/TermLife/DetailsPage'))
const QuotationPageGTL = lazy(() => import('Pages/Group/TermLife/QuotationPageGTL'))
const NomineeDetails = lazy(() => import('Pages/Group/TermLife/NomineeDetails'))
const PaymentTermLife = lazy(() => import('Pages/Group/TermLife/Payment'))
const ConsentGroup = lazy(() => import('Pages/Group/TermLife/Consent'))
const DetailsPageEMI = lazy(() => import('Pages/Group/EMIProtection/DetailsPageEMI'))
const ConsentGroupEMI = lazy(() => import('Pages/Group/EMIProtection/Consent'))
const PaymentEMI = lazy(() => import('Pages/Group/EMIProtection/PaymentEMI'))
const ConsentGroupFD = lazy(() => import('Pages/Group/FDProtect/Consent'))
const DetailsPageFD = lazy(() => import('Pages/Group/FDProtect/DetailsPageFD'))
const PaymentFD = lazy(() => import('Pages/Group/FDProtect/PaymentFD'))
const GroupHealth = lazy(() => import('Pages/Group/GroupHealth/DetailsPageGroup'))
const PaymentHealth = lazy(() => import('Pages/Group/GroupHealth/Payment'))
const ConsentGroupHealth = lazy(() => import('Pages/Group/GroupHealth/Consent'))
const AccountSelection = lazy(() => import('Pages/Group/FDProtect/AccountSelection'))
const NomineeDetailsFD = lazy(() => import('Pages/Group/FDProtect/NomineeDetails'))
const QuotationPageFD = lazy(() => import('Pages/Group/FDProtect/QuotationPageFD'))
const TravelQuotes = lazy(() => import('Pages/Sachet/Travel/TravelQuotes'))
const TravelCKYC = lazy(() =>
  import('Pages/Sachet/Travel/TravelCKYC').then((module) => ({ default: module.TravelCKYC })),
)
const TravelProposal = lazy(() => import('Pages/Sachet/Travel/Proposal'))
const TravelDetails = lazy(() => import('Pages/Sachet/Travel/Details'))
const TravelPayment = lazy(() => import('Pages/Sachet/Travel/Payment'))
const CustomerConsentAndPaymentConfirmation = lazy(
  () => import('Pages/Sachet/Travel/CustomerConsentAndPaymentConfirmation'),
)
const SpConsentFormTravel = lazy(() =>
  import('Pages/Sachet/Travel/TravelCKYC/SPConsent').then((module) => ({
    default: module.SpConsentFormTravel,
  })),
)
const CustomerConsentFormTravel = lazy(() =>
  import('Pages/Sachet/Travel/TravelCKYC/CustomerConsent').then((module) => ({
    default: module.CustomerConsentFormTravel,
  })),
)
const FireInitialCustomerConsent = lazy(
  () => import('Pages/Fire/Consent/FireInitialCustomerConsent'),
)
const Renewal = lazy(() =>
  import('Pages/Customer360/Renewal').then((module) => ({ default: module.Renewal })),
)
const RenewalHome = lazy(() =>
  import('Pages/Renewal').then((module) => ({ default: module.RenewalHome })),
)
const RenewalAccountDetail = lazy(() =>
  import('Pages/Renewal/RenewalAccountDetail').then((module) => ({
    default: module.RenewalAccountDetail,
  })),
)
const RenewalPayment = lazy(() =>
  import('Pages/Renewal/RenewalPayment').then((module) => ({ default: module.RenewalPayment })),
)
const EndorsementForm = lazy(() =>
  import('Pages/Customer360/NewEndorsement/Form').then((module) => ({
    default: module.EndorsementForm,
  })),
)
const ProposalForm = lazy(() =>
  import('Pages/Customer360/NewEndorsement/ProposalForm').then((module) => ({
    default: module.ProposalForm,
  })),
)
const EndorsementConsentForm = lazy(() =>
  import('Pages/Customer360/NewEndorsement/ConsentForm').then((module) => ({
    default: module.EndorsementConsentForm,
  })),
)
const RenewalCustomerConsent = lazy(() =>
  import('Pages/Customer360/Renewal/CustomerConsent').then((module) => ({
    default: module.CustomerConsent,
  })),
)
const ClaimProposalForm = lazy(() =>
  import('Pages/Customer360/ClaimForm/ClaimProposalForm').then((module) => ({
    default: module.ClaimProposalForm,
  })),
)
const NomineeDetialsHealth = lazy(() => import('Pages/Group/GroupHealth/NomineeDetailsHealth'))
const QuotationPageHealth = lazy(() => import('Pages/Group/GroupHealth/QuotationPageHealth'))
const CancellationForm = lazy(() =>
  import('Pages/Customer360/CancellationForm').then((module) => ({
    default: module.CancellationForm,
  })),
)
const ProposalCancellation = lazy(() =>
  import('Pages/Customer360/CancellationForm/ProposalCancellation').then((module) => ({
    default: module.ProposalCancellation,
  })),
)
const GrievanceForm = lazy(() =>
  import('Pages/Customer360/GrievanceForm').then((module) => ({ default: module.GrievanceForm })),
)
const ProposalGrievance = lazy(() =>
  import('Pages/Customer360/GrievanceForm/ProposalGrievance').then((module) => ({
    default: module.ProposalGrievance,
  })),
)
const SurrenderForm = lazy(() =>
  import('Pages/Customer360/SurrenderForm').then((module) => ({ default: module.SurrenderForm })),
)
const ProposalSurrender = lazy(() =>
  import('Pages/Customer360/SurrenderForm/ProposalSurrender').then((module) => ({
    default: module.ProposalSurrender,
  })),
)
const ReportDashboard = lazy(() =>
  import('Pages/Common/ReportDashboard').then((module) => ({ default: module.ReportDashboard })),
)
const BranchDashboard = lazy(() =>
  import('Pages/Common/ReportDashboard/BranchDashboard').then((module) => ({
    default: module.BranchDashboard,
  })),
)
const GroupHealthSalesReport = lazy(() =>
  import('Pages/Group/GroupHealth/SalesReport/GroupHealthSalesReport').then((module) => ({
    default: module.GroupHealthSalesReport,
  })),
)

const DIYPaymentSuccess = lazy(() =>
  import('Pages/Success').then((module) => ({ default: module.DIYPaymentSuccess })),
)

const Redirect = lazy(() =>
  import('Pages/Redirect').then((module) => ({ default: module.Redirect })),
)

// POSP module
const PospRegistrationStatus = lazy(() => import('PospModule/Pages/Posp/RegistrationStatus'))
const PospPanVerification = lazy(() => import('PospModule/Pages/Posp/PanVerification'))
const PospPersonalDetails = lazy(() => import('PospModule/Pages/Posp/PersonalDetails'))
const PospDocumentUpload = lazy(() => import('PospModule/Pages/Posp/DocumentUpload'))
const PospTrainingModule = lazy(() => import('PospModule/Pages/Posp/TrainingModule'))
const PospExamInstruction = lazy(() => import('PospModule/Pages/Posp/Examination/ExamInstruction'))
const PospGeneralInsurance = lazy(
  () => import('PospModule/Pages/Posp/Examination/GeneralInsurance'),
)
const PospExamResult = lazy(() => import('PospModule/Pages/Posp/Result'))
const PospEsign = lazy(() => import('PospModule/Pages/Posp/Esign'))

const PrivateRoutes = () => {
  const { isLoggedIn } = useAppSelector((state) => state.user)

  return isLoggedIn ? <Outlet /> : <Navigate to={'/login'} replace />
}

const RestrictedRoutes = () => {
  const { isLoggedIn } = useAppSelector((state) => state.user)

  return isLoggedIn ? <Navigate to={'/select-role'} replace /> : <Outlet />
}

export const AppRouter: React.FC = () => {
  const navigate = useNavigate()
  return (
    <Suspense
      fallback={
        <Box bgColor={'#fff'} height='100%' width='100%'>
          <LogoLoader />
        </Box>
      }
    >
      <Routes>
        <Route path='/test' element={<Test />} />
        <Route path='/redirect/*' element={<Redirect />} />
        {/* Public POSP eSign route — sent to customer, no auth required */}
        <Route path='/posp/esign/:uuid' element={<PospEsign />} />
        <Route path='' element={<PrivateRoutes />}>
          <Route path='/' element={<SellJourneyLayout />} />
          <Route path='/report-dashboard' element={<ReportDashboard />} />
          <Route path='/branch-dashboard' element={<BranchDashboard />} />

          <Route path='/health-plans' element={<SellJourneyLayout />} />
          <Route path='/customer-account' element={<SellJourneyLayout />} />
          <Route path='/renewal' element={<RenewalHome />} />
          <Route path='/renewal/customer-account' element={<RenewalAccountDetail />} />
          <Route path='/renewal/payment' element={<RenewalPayment />} />
          <Route path='/select-role' element={<RoleSelector />} />
          <Route path='/sp-licensing' element={<SpLicensing />} />
          <Route path='/sp-licensing/mcq' element={<SpLicensingMcq />} />
          <Route path='/sp-licensing/mcq/exam' element={<SpLicensingTakeExam />} />
          <Route path='/sp-licensing/mcq/result' element={<SpLicensingExamResult />} />
          <Route path='/sp-licensing/admin/questions' element={<SpLicensingAdminQuestions />} />
          <Route
            path='/sp-licensing/admin/manage-questions'
            element={<SpLicensingManageQuestions />}
          />
          {/* POSP module routes */}
          <Route path='posp/'>
            <Route path='registration-status' element={<PospRegistrationStatus />} />
            <Route path='sp-registration/pan-verification' element={<PospPanVerification />} />
            <Route path='sp-registration/personal-details' element={<PospPersonalDetails />} />
            <Route path='sp-registration/document-upload' element={<PospDocumentUpload />} />
            <Route path='sp-registration/training' element={<PospTrainingModule />} />
            <Route
              path='sp-registration/examination-instruction'
              element={<PospExamInstruction />}
            />
            <Route
              path='sp-registration/general-insurance-examination'
              element={<PospGeneralInsurance />}
            />
            <Route
              path='sp-registration/general-insurance-examination/result'
              element={<PospExamResult />}
            />
            <Route
              path='sp-registration/life-insurance-examination'
              element={<PospGeneralInsurance />}
            />
            <Route
              path='sp-registration/life-insurance-examination/result'
              element={<PospExamResult />}
            />
          </Route>
          <Route path='/lead-dashboard' element={<LeadListing />} />
          <Route path='/linked-account' element={<SellJourneyLayout />} />
          <Route path='/:type/customer-consent' element={<CustomerConsent />} />
          <Route path='/life/'>
            <Route path='life-assured' element={<SellJourneyLayout />} />
            <Route path='features' element={<SuitabilityMatrixSteps />} />
            <Route path='recommended-plans' element={<RecommendedPlans />} />
            <Route path='quotation' element={<LifeQuotation />} />
            <Route path='proposal' element={<BobLifeStepper />}></Route>
          </Route>
          <Route path='life-sales' element={<SalesReport />} />
          <Route path='sachet-sales' element={<SalesReportBgr key='home' />} />
          <Route path='fire-sales' element={<SalesReportBgr key='fire' lob='fire' />} />
          <Route path='health-sales' element={<HealthSalesReport />} />
          <Route path='group-health-sales' element={<GroupHealthSalesReport />} />
          <Route path='my-leads' element={<LeadListing />} />
          <Route path='/general/bgr'>
            <Route path='property-details' element={<PropertyDetails />} />
            <Route path='proposal-details' element={<ProposalDetails />} />
            <Route path='quotation' element={<QuotationBgr />} />
            <Route path='initial-customer-consent' element={<BgrInitialCustomerConsent />} />
            <Route path='sp-tagging' element={<BgrSpTagging />} />
            <Route path='proposal-customer-consent' element={<BgrProposalCustomerConsent />} />
            <Route path='payment' element={<BgrPayment />} />
          </Route>
          <Route path='/fire' element={<FireJourneyGuard />}>
            <Route path='home' element={<SellJourneyLayout />} />
            <Route path='property-questions' element={<FirePropertyQuestions />} />
            <Route path='ckyc' element={<CkycPage />} />
            <Route path='quotation' element={<QuotePage />} />
            <Route path='proposal' element={<ProposalPageFire />} />
            <Route path='sp-tagging' element={<FireSpTagging />} />
            <Route path='initial-customer-consent' element={<FireInitialCustomerConsent />} />
            <Route path='proposal-customer-consent' element={<FireProposalCustomerConsent />} />
          </Route>
          <Route path='/group'>
            <Route path='' element={<Navigate to='/health-plans' replace />} />
          </Route>
          <Route path='/travel/'>
            <Route path='home' element={<SellJourneyLayout />} />
            <Route path='travel-plans' element={<TravelQuotes />} />
            <Route path='ckyc' element={<TravelCKYC />} />
            <Route path='proposal' element={<TravelProposal />} />
            <Route path='details' element={<TravelDetails />} />
            <Route path='payment-selection' element={<TravelPayment />} />
            <Route
              path='customer-consent-and-payment'
              element={<CustomerConsentAndPaymentConfirmation />}
            />
          </Route>
        </Route>

        <Route path='/aboutus' element={<AboutUs />} />
        <Route path='/' element={<RestrictedRoutes />}>
          <Route path='login' element={<BankLogin />} />
        </Route>

        <Route
          path='/tessst'
          element={
            <>
              <TextInput />
              <BbButton>Unmount</BbButton>
            </>
          }
        />
        <Route path='group/'>
          <Route path='' element={<Navigate to='/health-plans' replace />} />
          <Route path='term-group' element={<DetailsPage />} />
          <Route path='term-group-quotations' element={<QuotationPageGTL />} />
          <Route path='term-group-nominee' element={<NomineeDetails />} />
          <Route path='term-group-payment' element={<PaymentTermLife />} />
          <Route path='consent-form-life' element={<ConsentGroup />} />
          <Route path='emi-protection' element={<DetailsPageEMI />} />
          <Route path='emi-payment' element={<PaymentEMI />} />
          <Route path='consent-form-emi' element={<ConsentGroupEMI />} />
          <Route path='fd-account-selection' element={<AccountSelection />} />
          <Route path='fd-protection' element={<DetailsPageFD />} />
          <Route path='fd-quotation' element={<QuotationPageFD />} />
          <Route path='fd-nominee-details' element={<NomineeDetailsFD />} />
          <Route path='fd-payment' element={<PaymentFD />} />
          <Route path='consent-form-fd' element={<ConsentGroupFD />} />
          <Route path='health-cover' element={<GroupHealth />} />
          <Route path='health-quotations' element={<QuotationPageHealth />} />
          <Route path='nominee-details-health' element={<NomineeDetialsHealth />} />
          <Route path='health-payment' element={<PaymentHealth />} />
          <Route path='consent-form-health' element={<ConsentGroupHealth />} />
          <Route path='payment-success' element={<DIYPaymentSuccess />} />
        </Route>

        <Route path='general/'>
          <Route
            path=''
            element={
              <SellJourneyLayout>
                <Flex
                  flexDir={'column'}
                  justifyContent='center'
                  boxSizing='border-box'
                  overflow={'hidden'}
                  p={'3rem'}
                  alignItems={'flex-start'}
                  bg='red'
                  w={{ lg: '60%', md: '100%', sm: '100%', xs: '100%' }}
                  minH={'70vh'}
                >
                  <Home />
                </Flex>
              </SellJourneyLayout>
            }
          />
          <Route path=':type/details' element={<VehicleInsuranceDetails />} />
          <Route path=':type/quotation' element={<Quotation />} />
          <Route path=':type/kyc' element={<CKYC />} />
          <Route path='sales' element={<MotorSalesReport />} />
          <Route path=':type/proposal' element={<MotorProposal />} />
          <Route path='consent-form/*' element={<CustomerConsentForm />} />
          <Route path='sp-consent/*' element={<SpConsentForm />} />
        </Route>

        <Route path='travel/'>
          <Route path='sp-consent/*' element={<SpConsentFormTravel />} />
          <Route path='proposal-customer-consent/*' element={<CustomerConsentFormTravel />} />
        </Route>

        <Route path={routeNames.customer360}>
          <Route path='' element={<Customer360 />} />
          <Route path={routeNames.renewal + '/payment/:productType'} element={<Renewal />} />
          <Route
            path={routeNames.renewal + '/consent/:productType/:consent_id/:tran_id'}
            element={<RenewalCustomerConsent />}
          />
          <Route path={routeNames.endorse} element={<EndorsementForm />} />
          <Route path={routeNames.cancellation} element={<CancellationForm />} />
          <Route path={routeNames.grievance} element={<GrievanceForm />} />
          <Route path={routeNames.surrender} element={<SurrenderForm />} />
          <Route path={routeNames.proposalForm} element={<ProposalForm />} />
          <Route path={routeNames.paymentSelection} element={<PaymentSelection />} />
          <Route path={routeNames.claim} element={<ClaimForm />} />
          <Route path={routeNames.claimProposalForm} element={<ClaimProposalForm />} />

          <Route path={routeNames.proposalCancel} element={<ProposalCancellation />} />
          <Route path={routeNames.grievanceForm} element={<ProposalGrievance />} />
          <Route path={routeNames.surrenderForm} element={<ProposalSurrender />} />
          <Route path={routeNames.consentForm} element={<EndorsementConsentForm />} />
          <Route path={routeNames.trackRequest} element={<TrackRequest />} />
          <Route path={routeNames.paymentSelection} element={<PaymentSelection />} />
          <Route path={routeNames.claim} element={<ClaimForm />} />
        </Route>
        <Route path='health/'>
          <Route
            path='consent-form/customer/consent_identifier/:consent_identifier'
            element={<ConsentForm type={'customer_consent'} />}
          />

          <Route
            path='consent-form/sp/consent_identifier/:consent_identifier'
            element={<ConsentForm type={'sp_consent'} />}
          />
          <Route path='' element={<PrivateRoutes />}>
            <Route path='member-details' element={<MemberDetails />} />
            <Route path='quotes/' element={<QuotationPage />} />

            <Route path='proposal/' element={<ProposalPage />}>
              <Route path='addons' element={<Addons />} />
              <Route
                path='ckyc'
                element={
                  <HealthCKYC
                    onPageNavigate={() => {
                      navigate('health/proposal/proposer-details', {
                        replace: true,
                      })
                    }}
                  />
                }
              />
              <Route path='proposer-details' element={<ProposerDetails />} />
              <Route path='insured-details' element={<InsuredDetails />} />
              <Route path='medical-health-questions' element={<MedicalHealthQuestions />} />
              <Route path='lifestyle-health-questions' element={<LifeStyleHealthQuestions />} />
              <Route path='payment-method' element={<PaymentMethod />} />
            </Route>
            <Route path='payment' element={<Payment />} />
          </Route>
        </Route>
        <Route path='/atf-test' element={<Test />} />
      </Routes>
    </Suspense>
  )
}
